import { AnimatePresence, motion } from 'motion/react'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { getStepDecimals, preventNumericInputChars } from '@pages/Trade/utils/helpers'

import { Body1, Body2, Caption1 } from '@ui/Typography'
import { IconButton } from '@ui/components/Button'
import { Input } from '@ui/components/Input/Input'
import { ListItem } from '@ui/components/List'
import { Popover, PopoverCard, PopoverList } from '@ui/components/Popover'
import { Toggle } from '@ui/components/Toggle/Toggle'
import { Tooltip } from '@ui/components/Tooltip'
import { ChevronIcon } from '@ui/icons/Chevron.icon'
import { CircleCheckIcon } from '@ui/icons/CircleCheck.icon'
import { InfoIcon } from '@ui/icons/Info.icon'
import { MinusIcon } from '@ui/icons/Minus.icon'
import { PlusIcon } from '@ui/icons/Plus.icon'
import type { IconFCType } from '@ui/icons/types'

import { useWindowResize } from '@utils/domUtils'
import { getCurrencySymbol } from '@utils/transaction.utils'

import { type ProtectiveOrderType } from '@model/DxTrade.types'
import type { CurrencyType } from '@model/WalletDto'

import { useFormatNumber } from '@hooks/useFormatNumber'

import { Box } from '@shared/components/Box'

import { formatAmountForInput, formatDistancePipsForDisplay } from '../../../utils/helpers'
import { useConversionRate } from '../../hooks/useConversionRatesStore'
import type { PortfolioTrade } from '../../hooks/useDxtradePortfolioTrades'
import type { PendingOrderWithLots } from '../../hooks/usePendingOrdersWithLots'
import { usePnLCalculation } from '../../hooks/usePnLCalculation'
import { getPnlColor } from '../../utils/formatters'
import { calculatePnL } from '../../utils/functions'
import {
  amountToPrice,
  formatPriceForInstrument,
  getOpenPriceAndQuantity,
  getProtectiveSign,
  isNumber,
  parsePriceInputValue,
  pipsFromPrice,
  priceFromPips,
} from '../../utils/tpslHelpers'
import { useProtectiveOrder } from './hooks/useProtectiveOrder'
import { useProtectiveOrderValidation } from './hooks/useProtectiveOrderValidation'
import type { ProtectiveOrderModeId } from './types'

import styles from './ProtectiveOrder.module.scss'

const collapseVariants = {
  hidden: { opacity: 0, height: 0 },
  visible: { opacity: 1, height: 'auto' },
}

const collapseTransition = { duration: 0.28, ease: 'easeInOut' as const }

interface ProtectiveOrderProps {
  title: string
  type: ProtectiveOrderType
  price: number | ''
  isActive: boolean
  onActiveChange: (value: boolean) => void
  onPriceChange: (value: number | '') => void
  position?: PortfolioTrade
  order?: PendingOrderWithLots
  accountCurrency?: CurrencyType
  instrumentCurrency?: CurrencyType
  instrumentMeta: { pipSize?: number | null; priceIncrement?: number | null }
  currentPrice?: number
  baseSide?: string | null
  basePrice?: number | null
  mode?: 'existing' | 'new'
  externalQuantity?: number | null
  externalOpenPrice?: number | null
  externalSide?: string | null
  onErrorChange?: (isError: boolean) => void
}

export const ProtectiveOrder: React.FC<ProtectiveOrderProps> = (props) => {
  const {
    title,
    price,
    isActive,
    onActiveChange,
    onPriceChange,
    position,
    order,
    type,
    currentPrice,
    baseSide,
    instrumentMeta = {},
    accountCurrency,
    instrumentCurrency,
    basePrice,
    mode = 'existing',
    externalQuantity,
    externalOpenPrice,
    externalSide,
    onErrorChange,
  } = props

  const isNewMode = mode === 'new'
  const isMobile = useWindowResize()
  const { t } = useTranslation()
  const { formatNumber } = useFormatNumber()

  const priceStepDecimals = getStepDecimals(instrumentMeta.priceIncrement)

  const { dropdownOptions, placeholdersById } = useProtectiveOrder(priceStepDecimals)

  const [selectedOption, setSelectedOption] = useState<ProtectiveOrderModeId>(dropdownOptions[0].id)
  const [isModeDropdownOpen, setIsModeDropdownOpen] = useState(false)
  const selectedModeLabel = useMemo(() => {
    return dropdownOptions.find((option) => option.id === selectedOption)?.name ?? ''
  }, [dropdownOptions, selectedOption])
  const isAmountMode = selectedOption === 'Amount'
  const isDistanceMode = selectedOption === 'Distance'
  const isPriceMode = !isAmountMode && !isDistanceMode

  const [priceInput, setPriceInput] = useState<string>('')
  const [amountInput, setAmountInput] = useState<string>('')
  const [distanceInput, setDistanceInput] = useState<string>('')
  const isPriceUserTypingRef = useRef(false)

  const positionData = getOpenPriceAndQuantity({ position, order })

  const openPrice = isNewMode ? externalOpenPrice : positionData.openPrice
  const quantity = isNewMode ? externalQuantity : positionData.quantity
  const side = isNewMode ? externalSide : positionData.side

  const pipSizeInitial = instrumentMeta.pipSize ?? null

  const sign = getProtectiveSign(type, side)

  const distanceBasePrice = useMemo<number | null>(() => {
    if (isNumber(currentPrice)) return currentPrice
    if (isNumber(basePrice)) return basePrice
    return null
  }, [basePrice, currentPrice])

  const [distancePips, setDistancePips] = useState<number | null>(() => {
    if (!isActive) return null
    const initial = pipsFromPrice({
      price: isNumber(price) ? price : null,
      basePrice: distanceBasePrice,
      pipSize: pipSizeInitial,
      sign,
    })
    return isNumber(initial) ? Math.max(0, initial as number) : null
  })

  const exitPrice = isNumber(price) ? price : null

  const instrumentCurrencyResolved =
    instrumentCurrency ?? position?.instrumentCurrency ?? order?.instrumentCurrency ?? null

  const { rate, requiresConversion } = useConversionRate(
    instrumentCurrencyResolved,
    accountCurrency
  )

  const estimatedPnlFromHook = usePnLCalculation({
    position,
    order,
    exitPrice,
    accountCurrency,
    instrumentCurrency,
    conversionRate: rate,
  })

  const estimatedPnlDirectBase = useMemo(
    () => (isNewMode ? calculatePnL({ side, openPrice, quantity, exitPrice }) : null),
    [isNewMode, side, openPrice, quantity, exitPrice]
  )
  const estimatedPnlDirect = useMemo(() => {
    if (estimatedPnlDirectBase == null) return null
    if (!requiresConversion) return estimatedPnlDirectBase
    if (rate == null) return null
    return estimatedPnlDirectBase * rate
  }, [estimatedPnlDirectBase, requiresConversion, rate])
  const estimatedPnl = isNewMode ? estimatedPnlDirect : estimatedPnlFromHook
  const isTakeProfit = type === 'takeProfit'

  const isStopLossShowingProfit = !isTakeProfit && isNumber(estimatedPnl) && estimatedPnl > 0
  const isTakeProfitShowingLoss = isTakeProfit && isNumber(estimatedPnl) && estimatedPnl < 0
  const zeroLossPrice = isStopLossShowingProfit && isNumber(openPrice) ? openPrice : null
  const zeroProfitPrice = isTakeProfitShowingLoss && isNumber(openPrice) ? openPrice : null
  const isShowingInvalidPnl = isStopLossShowingProfit || isTakeProfitShowingLoss
  const zeroResetPrice = zeroLossPrice ?? zeroProfitPrice

  const derivedAmount = useMemo(() => {
    if (!isNumber(estimatedPnl)) return null
    if (isTakeProfit) {
      return estimatedPnl > 0 ? estimatedPnl : 0
    }
    return estimatedPnl < 0 ? -estimatedPnl : 0
  }, [estimatedPnl, isTakeProfit])

  const handleModeSelect = (nextId: ProtectiveOrderModeId) => {
    if (nextId === 'Price') {
      if (isNumber(price)) {
        const formatted = formatPriceForInstrument(price, instrumentMeta)
        setPriceInput(String(formatted ?? ''))
      } else {
        setPriceInput('')
      }
    }

    if (nextId === 'Amount') {
      const amountValue = isShowingInvalidPnl ? 0 : derivedAmount
      setAmountInput(formatAmountForInput(amountValue))
      if (isShowingInvalidPnl && zeroResetPrice != null && price !== zeroResetPrice) {
        onPriceChange(zeroResetPrice)
        const formattedZeroPrice = formatPriceForInstrument(zeroResetPrice, instrumentMeta)
        setPriceInput(String(formattedZeroPrice ?? zeroResetPrice))
      }
    }

    if (nextId === 'Distance') {
      if (isNumber(distancePips)) {
        setDistanceInput(formatDistancePipsForDisplay(Math.max(0, distancePips as number)))
      } else {
        setDistanceInput('')
      }
    }

    setSelectedOption(nextId)
  }

  const validationBasePrice = !isNewMode && order && isNumber(openPrice) ? openPrice : currentPrice

  const { effectiveIsError, validationMessage } = useProtectiveOrderValidation({
    type,
    selectedOption,
    price,
    amountInput,
    distanceInput,
    isTakeProfit,
    baseSide,
    validationBasePrice,
    priceStepDecimals,
    isActive,
    openPrice,
    quantity,
    requiresConversion,
    rate,
    accountCurrency,
    distanceBasePrice,
    pipSizeInitial,
  })

  const reportedError = isActive && effectiveIsError
  useEffect(() => {
    onErrorChange?.(reportedError)
  }, [onErrorChange, reportedError])

  const handlePriceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isAmountMode) {
      const rawAmount = event.target.value
      setAmountInput(rawAmount)

      const parsedAmount = parsePriceInputValue(rawAmount)
      if (parsedAmount === '') {
        onPriceChange('')
        return
      }
      const absoluteAmount = Math.abs(parsedAmount)
      const signedAmount = isTakeProfit ? absoluteAmount : -absoluteAmount

      const amountInInstrumentCurrency =
        requiresConversion && rate ? signedAmount / rate : signedAmount

      const nextPrice = amountToPrice({
        side,
        openPrice,
        quantity,
        amount: amountInInstrumentCurrency,
        priceIncrement: instrumentMeta.priceIncrement,
      })
      onPriceChange(nextPrice ?? '')
      return
    }

    if (isDistanceMode) {
      const rawDistance = event.target.value
      setDistanceInput(rawDistance)

      const parsedPips = parsePriceInputValue(rawDistance)
      if (parsedPips === '') {
        onPriceChange('')
        setDistancePips(null)
        return
      }
      const normalizedPips = Math.max(0, parsedPips as number)
      const pipSize = instrumentMeta.pipSize ?? null
      const sign = getProtectiveSign(type, side)
      const nextPrice = priceFromPips({
        basePrice: distanceBasePrice,
        pipSize,
        priceIncrement: instrumentMeta.priceIncrement,
        pips: normalizedPips,
        sign,
      })
      setDistancePips(normalizedPips)
      onPriceChange(nextPrice ?? '')
      return
    }
    const raw = event.target.value

    isPriceUserTypingRef.current = true
    setPriceInput(raw)
    if (raw === '') {
      onPriceChange('')
    } else {
      onPriceChange(parsePriceInputValue(raw))
    }
  }

  const baseForDistance = distanceBasePrice
  const pipSizeForDistance = instrumentMeta.pipSize ?? null
  const distanceValue =
    isNumber(distancePips) && pipSizeForDistance != null
      ? Math.max(0, distancePips as number) * pipSizeForDistance
      : null
  const distanceDisplayPips = isNumber(distancePips) ? Math.max(0, distancePips as number) : null
  const distancePercent =
    isNumber(distanceValue) && isNumber(baseForDistance) && baseForDistance !== 0
      ? ((distanceValue as number) / Math.abs(baseForDistance as number)) * 100
      : null

  const { formattedEstimatedPnl, displayedPnlForColor } = useMemo(() => {
    const pnlValue = typeof estimatedPnl === 'number' ? estimatedPnl : 0
    const currencySymbol = accountCurrency ? getCurrencySymbol(accountCurrency) : ''
    const formatValue = (value: number) => {
      if (value === 0) return '0'
      const formatted = value.toFixed(2)
      return currencySymbol ? `${currencySymbol}${formatted}` : formatted
    }

    if (isTakeProfit) {
      const profitValue = pnlValue > 0 ? pnlValue : 0
      const formatted = profitValue === 0 ? formatValue(0) : `+${formatValue(profitValue)}`
      return { formattedEstimatedPnl: formatted, displayedPnlForColor: profitValue }
    }

    const lossValue = pnlValue < 0 ? Math.abs(pnlValue) : 0
    const formatted = lossValue === 0 ? formatValue(0) : `-${formatValue(lossValue)}`
    return {
      formattedEstimatedPnl: formatted,
      displayedPnlForColor: lossValue === 0 ? 0 : -lossValue,
    }
  }, [accountCurrency, estimatedPnl, isTakeProfit])
  const pnlColor = getPnlColor(displayedPnlForColor)

  const PnlInfo = () => (
    <Box flex align='center' gap='x4'>
      <Body2 color='text-med-em'>
        {isTakeProfit ? t('Portfolio.Estimated profit') : t('Portfolio.Estimated loss')}
      </Body2>
      <Body2 color={pnlColor} className={styles.numericValue} sensitive>
        {formattedEstimatedPnl}
      </Body2>
    </Box>
  )

  const PriceInfo = () => (
    <Box flex align='center' gap='x4'>
      <Body2 color='text-med-em'>{t('Portfolio.Price')}:</Body2>
      <Body2 color='text-high-em' className={styles.numericValue}>
        {isNumber(price)
          ? formatNumber(price, {
              minDecimalPlaces: priceStepDecimals,
              maxDecimalPlaces: priceStepDecimals,
            })
          : '-'}
      </Body2>
    </Box>
  )

  const DistanceInfo = () =>
    distanceDisplayPips !== null ? (
      <Box flex align='center' gap='x4'>
        <Body2 color='text-med-em'>{t('Portfolio.Distance')}:</Body2>
        <Body2 color='text-high-em' className={styles.numericValue}>
          {formatDistancePipsForDisplay(distanceDisplayPips)}
        </Body2>
        {distancePercent !== null && (
          <Body2 color='text-high-em' className={styles.numericValue}>
            ({distancePercent.toFixed(2)}%)
          </Body2>
        )}
      </Box>
    ) : null

  const renderPriceInfo = () => {
    if (isPriceMode) {
      return (
        <Box flex align='center' gap='x12' wrap>
          <PnlInfo />
          <DistanceInfo />
        </Box>
      )
    }

    if (isAmountMode) {
      return (
        <Box flex align='center' gap='x12' wrap>
          <PriceInfo />
          <DistanceInfo />
        </Box>
      )
    }

    return (
      <Box flex align='center' gap='x12' wrap>
        <PnlInfo />
        <PriceInfo />
      </Box>
    )
  }

  const inputValue = isAmountMode ? amountInput : isDistanceMode ? distanceInput : priceInput

  const amountCurrencySymbol =
    isAmountMode && accountCurrency ? getCurrencySymbol(accountCurrency) : null
  const amountCurrencyIcon = useMemo<IconFCType | undefined>(() => {
    if (!amountCurrencySymbol) return undefined
    const CurrencyIcon: IconFCType = Object.assign(() => <span>{amountCurrencySymbol}</span>, {
      __isValidIcon: true as const,
    })
    return CurrencyIcon
  }, [amountCurrencySymbol])

  const priceStepValue = instrumentMeta.priceIncrement ?? null
  const amountStepValue = 0.01
  const distanceStepValue = 1
  const decimalPlaces = useMemo<number | undefined>(() => {
    if (isPriceMode) {
      return priceStepDecimals ?? undefined
    }
    if (isAmountMode) {
      return 2
    }
    return 0
  }, [isAmountMode, isPriceMode, priceStepDecimals])

  const currentValue = () => {
    if (isPriceMode) {
      return isNumber(price) ? price : 0
    }
    if (isAmountMode) {
      const n = Number(amountInput)
      return Number.isFinite(n) ? n : 0
    }
    if (isDistanceMode) {
      const n = Number(distanceInput)
      return Number.isFinite(n) ? n : 0
    }
    return 0
  }

  const handleStepChange = (direction: 1 | -1) => {
    if (!isActive) return

    if (isPriceMode) {
      if (!isNumber(priceStepValue) || priceStepDecimals == null) return
      const nextValue = Number(
        (currentValue() + direction * priceStepValue).toFixed(priceStepDecimals)
      )
      if (nextValue < 0) return
      onPriceChange(nextValue)
      const formatted = formatPriceForInstrument(nextValue, instrumentMeta)
      setPriceInput(String(formatted ?? nextValue))
      return
    }

    if (isAmountMode) {
      let nextValue = Number((currentValue() + direction * amountStepValue).toFixed(2))
      if (nextValue < 0) nextValue = 0
      setAmountInput(nextValue.toFixed(2))

      const signedAmount = isTakeProfit ? nextValue : -nextValue

      const amountInInstrumentCurrency =
        requiresConversion && rate ? signedAmount / rate : signedAmount

      const nextPrice = amountToPrice({
        side,
        openPrice,
        quantity,
        amount: amountInInstrumentCurrency,
        priceIncrement: instrumentMeta.priceIncrement,
      })
      onPriceChange(nextPrice ?? '')
      return
    }

    if (isDistanceMode) {
      let nextValue = currentValue() + direction * distanceStepValue
      if (nextValue < 0) nextValue = 0
      setDistanceInput(String(nextValue))
      setDistancePips(nextValue)
      const pipSizeValue = instrumentMeta.pipSize ?? null
      const basePriceForStep = distanceBasePrice
      const signValue = getProtectiveSign(type, side)
      if (!pipSizeValue || !isNumber(basePriceForStep) || !signValue) return
      const nextPrice = priceFromPips({
        basePrice: basePriceForStep as number,
        pipSize: pipSizeValue,
        pips: nextValue,
        sign: signValue,
        priceIncrement: instrumentMeta.priceIncrement,
      })
      onPriceChange(nextPrice ?? '')
    }
  }

  const stepValue = isPriceMode
    ? isNumber(priceStepValue) && priceStepDecimals != null
      ? priceStepValue
      : null
    : isAmountMode
    ? amountStepValue
    : distanceStepValue
  const isDecreaseDisabled = !isActive || !isNumber(stepValue) || currentValue() - stepValue < 0
  const isIncreaseDisabled = !isActive || !isNumber(stepValue)

  useEffect(() => {
    if (!isActive) {
      setDistancePips(null)
      setPriceInput('')
      setAmountInput('')
      setDistanceInput('')
    }
  }, [isActive])

  useEffect(() => {
    if (!isActive || !isNumber(price)) {
      setDistancePips(null)
      return
    }

    if (isDistanceMode && isNumber(distancePips)) {
      const pipSize = instrumentMeta.pipSize ?? null
      const signValue = getProtectiveSign(type, side)
      if (!pipSize || !signValue || !isNumber(distanceBasePrice)) return
      const nextPrice = priceFromPips({
        basePrice: distanceBasePrice,
        pipSize,
        pips: distancePips,
        sign: signValue,
        priceIncrement: instrumentMeta.priceIncrement,
      })
      if (isNumber(nextPrice) && nextPrice !== price) {
        onPriceChange(nextPrice as number)
      }
      return
    }

    const pipSize = instrumentMeta.pipSize ?? null
    const base = distanceBasePrice
    const sign = getProtectiveSign(type, side)
    const pips = pipsFromPrice({
      price: price as number,
      basePrice: base,
      pipSize,
      sign,
    })
    setDistancePips(isNumber(pips) ? Math.max(0, pips as number) : null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    price,
    isActive,
    distanceBasePrice,
    instrumentMeta.pipSize,
    instrumentMeta.priceIncrement,
    type,
    side,
    isDistanceMode,
    distancePips,
    onPriceChange,
  ])

  useEffect(() => {
    if (!isActive) return
    if (!isPriceMode) return
    if (isPriceUserTypingRef.current) return
    if (!isNumber(price)) return
    const formatted = formatPriceForInstrument(price as number, instrumentMeta)
    setPriceInput(String(formatted ?? ''))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, isPriceMode, price])

  useEffect(() => {
    if (!isActive) return
    if (!isDistanceMode) return
    if (distanceInput !== '') return
    if (isNumber(distancePips)) {
      setDistanceInput(formatDistancePipsForDisplay(Math.max(0, distancePips as number)))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, isDistanceMode, distancePips])

  useEffect(() => {
    if (!isActive) return
    if (!isAmountMode) return
    if (amountInput === '') {
      const defaultAmountValue = isShowingInvalidPnl ? 0 : derivedAmount
      setAmountInput(formatAmountForInput(defaultAmountValue))
      if (isShowingInvalidPnl && zeroResetPrice != null && price !== zeroResetPrice) {
        onPriceChange(zeroResetPrice)
        const formattedZeroPrice = formatPriceForInstrument(zeroResetPrice, instrumentMeta)
        setPriceInput(String(formattedZeroPrice ?? zeroResetPrice))
      }
      return
    }

    if (isNewMode) {
      const parsedAmount = parsePriceInputValue(amountInput)
      if (parsedAmount !== '' && isNumber(openPrice) && isNumber(quantity)) {
        const absoluteAmount = Math.abs(parsedAmount as number)
        const signedAmount = isTakeProfit ? absoluteAmount : -absoluteAmount

        const amountInInstrumentCurrency =
          requiresConversion && rate ? signedAmount / rate : signedAmount

        const nextPrice = amountToPrice({
          side,
          openPrice,
          quantity,
          amount: amountInInstrumentCurrency,
          priceIncrement: instrumentMeta.priceIncrement,
        })
        if (isNumber(nextPrice) && nextPrice !== price) {
          onPriceChange(nextPrice as number)
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isActive,
    isAmountMode,
    derivedAmount,
    isShowingInvalidPnl,
    zeroResetPrice,
    price,
    instrumentMeta,
    onPriceChange,
    isNewMode,
    amountInput,
    openPrice,
    quantity,
    side,
    isTakeProfit,
  ])

  const handlePriceBlur = () => {
    isPriceUserTypingRef.current = false
    if (!isActive) return

    if (isPriceMode) {
      if (!isNumber(price)) return
      const formatted = formatPriceForInstrument(price as number, instrumentMeta)
      setPriceInput(String(formatted ?? ''))
      return
    }

    if (isAmountMode && amountInput !== '') {
      const n = Number(amountInput)
      if (Number.isFinite(n)) setAmountInput(n.toFixed(2))
      return
    }

    if (isDistanceMode && distanceInput !== '') {
      const n = Number(distanceInput)
      if (Number.isFinite(n)) setDistanceInput(String(n))
    }
  }

  return (
    <Box
      flex
      direction='column'
      px={isNewMode ? undefined : 'x16'}
      py={isNewMode ? undefined : 'x16'}
      radius='x16'
      border={isNewMode ? undefined : 'outline-low-em'}
    >
      <Box flex justify='space-between' gap='x12'>
        <Box flex align='center' gap='x6'>
          <Body1 medium>{title}</Body1>
          <Tooltip
            content={
              isTakeProfit
                ? t('OrderWidget.Take Profit Tooltip')
                : t('OrderWidget.Stop Loss Tooltip')
            }
            direction='top'
            heading={isMobile ? title : undefined}
            secondaryButton={isMobile ? { label: t('Close') } : undefined}
          >
            <InfoIcon size={20} color='text-low-em' />
          </Tooltip>
        </Box>
        <Toggle name='protective' value={isActive} onChange={onActiveChange} />
      </Box>

      <AnimatePresence>
        {isActive && (
          <motion.div
            variants={collapseVariants}
            initial='hidden'
            animate='visible'
            exit='hidden'
            transition={collapseTransition}
            className={styles.collapseContent}
          >
            <Box flex gap='x12' pt='x12' fullWidth>
              <Box flex align='center' gap='x8' fullWidth className={styles.mainInputColumn}>
                <IconButton
                  variant='secondary'
                  size='md'
                  icon={MinusIcon}
                  onClick={() => handleStepChange(-1)}
                  disabled={isDecreaseDisabled}
                />
                <Input
                  value={inputValue}
                  onChange={handlePriceChange}
                  onBlur={handlePriceBlur}
                  onKeyDown={preventNumericInputChars}
                  onWheel={(e) => e.currentTarget.blur()}
                  placeholder={placeholdersById[selectedOption]}
                  label=''
                  fullWidth
                  size='lg'
                  variant='default'
                  type='number'
                  inputMode='decimal'
                  decimalPlaces={decimalPlaces}
                  min={0}
                  step={stepValue ?? undefined}
                  showLabel={false}
                  error={effectiveIsError}
                  leftIcon={amountCurrencyIcon}
                />
                <IconButton
                  variant='secondary'
                  size='md'
                  icon={PlusIcon}
                  onClick={() => handleStepChange(1)}
                  disabled={isIncreaseDisabled}
                />
              </Box>
              <Box flex fullWidth className={styles.modeDropdownColumn}>
                <Popover
                  isOpen={isModeDropdownOpen}
                  setIsOpen={setIsModeDropdownOpen}
                  label={t('Mode')}
                  content={
                    <PopoverCard>
                      <PopoverList>
                        {dropdownOptions.map((option) => {
                          const isActive = option.id === selectedOption
                          return (
                            <ListItem
                              key={option.id}
                              onClick={() => {
                                handleModeSelect(option.id)
                                setIsModeDropdownOpen(false)
                              }}
                              active={isActive}
                              endSlot={
                                isActive
                                  ? {
                                      icon: {
                                        icon: {
                                          icon: CircleCheckIcon,
                                          color: 'text-purple-high-em',
                                        },
                                      },
                                    }
                                  : undefined
                              }
                            >
                              {option.name}
                            </ListItem>
                          )
                        })}
                      </PopoverList>
                    </PopoverCard>
                  }
                >
                  <Input
                    value={selectedModeLabel}
                    readOnly
                    size='lg'
                    showLabel={false}
                    placeholder={t('Mode')}
                    rightIcon={ChevronIcon.Down}
                    onWrapperClick={() => setIsModeDropdownOpen((prev) => !prev)}
                    onEndIconClick={() => setIsModeDropdownOpen((prev) => !prev)}
                    fullWidth
                    label=''
                  />
                </Popover>
              </Box>
            </Box>
            {validationMessage && (
              <Box pt='x4'>
                <Caption1 color='text-danger-high-em'>{validationMessage}</Caption1>
              </Box>
            )}
            <Box mt='x12'>
              <Body2 color='text-med-em'>{renderPriceInfo()}</Body2>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  )
}
